import { type PageIntroHighlight } from '@/components/molecules';
import { type IntroMediaName } from '@/config/introVehicles';

import { type PageIntroSlide, usePageIntroSlides } from './usePageIntroSlides';

/**
 * Every field spells out `| undefined` because the app compiles with
 * `exactOptionalPropertyTypes`. Under that flag an absent property and one
 * explicitly set to `undefined` are different types, and `PageIntro`
 * destructures these out of its props and hands them over as a fresh object —
 * which makes each of them `T | undefined` whether the page passed it or not.
 * Pages are unaffected: omitting a field was legal before and still is.
 */
export type PageIntroInput = {
  /** Loop cars behind the copy. Opt-in; see PageIntro. */
  slideshow?: boolean | undefined;
  /**
   * Overrides what loops behind the copy — cars, or the accessory cut-outs
   * /accessories names. Defaults to DEFAULT_INTRO_SLIDES.
   */
  slides?: readonly IntroMediaName[] | undefined;
  /** Proof points for the trust strip. */
  highlights?: readonly PageIntroHighlight[] | undefined;
  /**
   * Keep the band on a phone, where it is otherwise not drawn at all.
   *
   * The default is the e-commerce answer, and it is the one the measurements
   * argued for: on a phone this band and the first `SectionHeading` under it
   * were two heading blocks of the same shape, one after the other, saying
   * close to the same thing — "TAX CALCULATOR / Estimate your URA import
   * taxes" and then "THE CALCULATOR / Two answers: what URA values your car
   * at", or "DELIVERED CARS" printed twice down the gallery. A catalogue site
   * does not open a category with a masthead; it opens it with the catalogue.
   *
   * Five pages pass this, and they are the five where the band is the page's
   * *only* heading rather than a second one. Hide it on /cars/cart and a populated
   * car cart has no title at all; hide it on /cars/submitted and
   * "Thank you — we have your request", which is the entire message of the
   * page, is gone; hide it on the legal pages and the document starts at its
   * meta strip with nothing saying which document it is.
   *
   * The test for a new page is that question and not its importance: does the
   * content under the band introduce itself? If it does, the band is
   * duplication on a phone. If it does not, the band is the heading.
   */
  keepOnPhone?: boolean | undefined;
};

/**
 * The two shapes the opening band comes in.
 *
 * A discriminated union rather than a bag of optional fields, so a branch that
 * has `slides` cannot also be told it is writing in dark ink by accident — the
 * two travel together or not at all, which is the invariant the band kept
 * getting wrong while `stage` was a free boolean passed down beside them.
 */
type WithHighlights = {
  /** Non-empty, or null. The band never renders an empty strip. */
  highlights: readonly PageIntroHighlight[] | null;
  /**
   * Whether the band is drawn below `md`.
   *
   * It is `display`, not a branch in the tree: the page is prerendered once
   * for every width, so the band is in the HTML either way and only CSS can
   * still tell a phone from a desktop afterwards. A crawler and a desktop
   * reader both see the h1; a phone sees the content.
   */
  onPhone: boolean;
};

export type PageIntroPlainLayout = WithHighlights & {
  variant: 'plain';
  stage: false;
  slides: null;
};

export type PageIntroFeatureLayout = WithHighlights & {
  variant: 'feature';
  /**
   * The band wears the dark stage — from `md` up, which is the only width at
   * which the stage exists at all (pageIntroBand.ts). Below it both layouts
   * are a title block on the page's own surface, so everything downstream
   * reads this as "which pair of values", not "which colour".
   */
  stage: true;
  slides: readonly PageIntroSlide[];
};

/**
 * Written out as two named branches rather than one object intersected with a
 * union, so each layout has a type an organism can `Pick` from. Picked off the
 * intersection, `slides` comes back as `readonly PageIntroSlide[] | null` on
 * both branches, which hands the cinematic band a null it has already been
 * proved cannot reach it.
 */
export type PageIntroLayout = PageIntroPlainLayout | PageIntroFeatureLayout;

/**
 * Everything the opening band has to decide before it can draw itself.
 *
 * Three decisions, all of them previously inline in the organism and two of
 * them repeated at each branch: which of the two layouts this page asked for,
 * whether that layout wears the stage, and whether there are enough proof
 * points to be worth a strip. Pulling them here leaves the organism holding
 * structure only, and makes the tone impossible to state independently of the
 * surface it is stated against — the stage is dark in *both* colour schemes
 * and absent below `md`, so a page that got those out of step printed
 * near-black text on near-black, or white text on white.
 *
 * `highlights` is normalised rather than passed through: a page that hands
 * over an empty array means the same thing as a page that hands over nothing,
 * and only one of those two should reach the view.
 */
export function usePageIntroLayout({
  slideshow = false,
  slides: names,
  highlights,
  keepOnPhone = false,
}: PageIntroInput): PageIntroLayout {
  const slides = usePageIntroSlides(slideshow, names);

  // Not memoised, deliberately. Every page passes `highlights` as a literal,
  // so a memo keyed on it would rebuild on every render anyway, and nothing
  // downstream depends on the identity — the strip maps over it during render
  // and no effect or query is keyed on it. `usePageIntroSlides` memoises
  // because it *builds* objects; this only chooses between two it was given.
  const items = highlights && highlights.length > 0 ? highlights : null;

  return slides
    ? { variant: 'feature', stage: true, slides, highlights: items, onPhone: keepOnPhone }
    : { variant: 'plain', stage: false, slides: null, highlights: items, onPhone: keepOnPhone };
}
